import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Loading from './Loading';
import 'fslightbox';

const ExhibitorProfile = ({
  exhibitorId,
  conventionId,
  onClose,
  isLargeScreen,
}) => {
  const [exhibitor, setExhibitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!exhibitorId) return;

    const fetchExhibitor = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch the exhibitor profile - the show endpoint now includes all locations
        const response = await axios.get(
          `/api/v1/exhibitor_profiles/${exhibitorId}`,
          {
            params: { c: conventionId },
          },
        );

        const exhibitorData = response.data;
        setExhibitor(exhibitorData);

        // Initialize fslightbox after exhibitor data is loaded
        setTimeout(() => {
          if (window.refreshFsLightbox) {
            window.refreshFsLightbox();
          }
        }, 100);
      } catch (err) {
        setError(
          err.response?.data?.error || 'Failed to load exhibitor profile',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchExhibitor();
  }, [exhibitorId, conventionId]);

  const getExhibitorTypeIcon = type => {
    // Map the React component types to backend exhibitor_type keys
    const typeMapping = {
      Artists: 'artist',
      Authors: 'author',
      'Community Groups': 'community_group',
      Entertainers: 'entertainer',
      'Food & Drink': 'food_drink_vendor',
      Makers: 'maker',
      Sponsors: 'sponsor',
      Exhibitors: 'exhibitor',
    };

    // Get the backend type key
    const backendType = typeMapping[type] || 'exhibitor';

    // Use the exact icons from Ruby EXHIBITOR_TYPE_ICONS
    switch (backendType) {
      case 'artist':
        return 'fa-paint-brush';
      case 'author':
        return 'fa-book';
      case 'community_group':
        return 'fa-group';
      case 'entertainer':
        return 'fa-music';
      case 'food_drink_vendor':
        return 'fa-cutlery';
      case 'maker':
        return 'fa-gavel';
      case 'sponsor':
        return 'fa-star';
      case 'exhibitor':
      default:
        return 'fa-shopping-cart';
    }
  };

  const getExhibitorTypeSingular = type => {
    switch (type) {
      case 'Artists':
        return 'Artist';
      case 'Authors':
        return 'Author';
      case 'Community Groups':
        return 'Community Group';
      case 'Entertainers':
        return 'Entertainer';
      case 'Food & Drink':
        return 'Food & Drink';
      case 'Makers':
        return 'Maker';
      case 'Sponsors':
        return 'Sponsor';
      case 'Exhibitors':
      default:
        return 'Exhibitor';
    }
  };

  const getExhibitorTypeDisplay = (type, isSponsor, title) => {
    const singularType = getExhibitorTypeSingular(type);
    let displayText = singularType;

    // Add title if it exists
    if (title) {
      displayText += ` - ${title}`;
    }

    return displayText;
  };

  const handleLinkClick = (linkType, url) => {
    // Analytics tracking
    if (typeof gtag === 'function') {
      gtag('event', 'exhibitor_link_click', {
        exhibitor_name: exhibitor.name,
        exhibitor_id: exhibitor.id,
        link_type: linkType,
        link_url: url,
      });
    }
  };

  const renderLocations = () => {
    const locationsToShow = exhibitor.locations || [];

    if (locationsToShow.length === 0) return null;

    return (
      <div className="exhibitor-locations-section">
        {locationsToShow.map((location, index) => {
          // Clean up the location label by removing extra colons and formatting properly
          let formattedLabel = location.label;
          if (formattedLabel) {
            // Remove trailing colons and spaces
            formattedLabel = formattedLabel.replace(/\s*:\s*$/, '');
          }

          return (
            <div key={index} className="events">
              <a
                className="tracklink"
                href={location.navigateTo}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleLinkClick('location', location.navigateTo)}
              >
                <i className="fa fa-map-marker"></i>
                <span>{formattedLabel}</span>
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="profile-card loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-card error">
        <div className="error-message">
          <h3>Error Loading Profile</h3>
          <p>{error}</p>
          <button onClick={onClose} className="button">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!exhibitor) {
    return (
      <div className="profile-card error">
        <div className="error-message">
          <h3>Exhibitor Not Found</h3>
          <button onClick={onClose} className="button">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="profile-card"
      style={{
        position: isLargeScreen ? 'sticky' : 'relative',
        top: isLargeScreen ? '0' : 'auto',
        maxHeight: isLargeScreen ? '100vh' : 'none',
        overflowY: isLargeScreen ? 'auto' : 'visible',
        overflowX: 'hidden',
        width: !isLargeScreen ? '100%' : 'auto',
        maxWidth: !isLargeScreen ? '600px' : 'none',
        margin: !isLargeScreen ? '0 auto' : '0',
      }}
    >
      <section id="card-wrapper">
        <section
          id="main"
          style={{ paddingBottom: '20px', position: 'relative' }}
        >
          {!isLargeScreen && (
            <div
              className="profile-header"
              style={{ marginBottom: '0.25rem', padding: '0.25rem' }}
            >
              <button className="close-button" onClick={onClose}>
                <i className="fa fa-arrow-left"></i>
                <span>Back to List</span>
              </button>
            </div>
          )}

          {isLargeScreen && (
            <button
              type="button"
              className="button profile-close-button"
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                padding: '6px 12px',
                fontSize: '14px',
                zIndex: 10,
                marginRight: 0,
              }}
            >
              X
            </button>
          )}

          <h1 id="title-exhibitor">{exhibitor.name}</h1>
          <div id="exhibitor-type">
            {exhibitor.isSponsor ? (
              <i className="fa fa-star sponsor-star" title="Sponsor"></i>
            ) : (
              <i
                className={`fa ${getExhibitorTypeIcon(
                  exhibitor.exhibitorType,
                  exhibitor.isSponsor,
                )}`}
              ></i>
            )}
            {getExhibitorTypeDisplay(
              exhibitor.exhibitorType,
              exhibitor.isSponsor,
              exhibitor.title,
            )}
          </div>

          <div className="flex">
            <div id="avatar-container">
              {exhibitor.avatarUrl && (
                <img
                  className="avatar no-border"
                  src={exhibitor.avatarUrl}
                  alt={`${exhibitor.name} logo`}
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>

            <div>
              <div id="exhibitor-links">
                {renderLocations()}

                {exhibitor.genConEvents && (
                  <div className="events">
                    <a
                      className="tracklink"
                      href={exhibitor.genConEvents.navigateTo}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleLinkClick(
                          'events',
                          exhibitor.genConEvents.navigateTo,
                        )
                      }
                    >
                      <i className="fa fa-ticket"></i>
                      <span>{exhibitor.genConEvents.label}</span>
                    </a>
                  </div>
                )}

                {exhibitor.website && (
                  <div className="website">
                    <a
                      className="tracklink"
                      href={exhibitor.website.navigateTo}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleLinkClick('website', exhibitor.website.navigateTo)
                      }
                    >
                      <i className="fa fa-external-link"></i>
                      <span>{exhibitor.website.label}</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {exhibitor.tags && exhibitor.tags.length > 0 && (
            <div id="tags">
              <ul>
                {exhibitor.tags.map((tag, index) => (
                  <li key={index}>{tag}</li>
                ))}
              </ul>
            </div>
          )}

          {exhibitor.description && (
            <div id="exhibitor-description">{exhibitor.description}</div>
          )}

          {exhibitor.images && exhibitor.images.length > 0 && (
            <div id="featured-images">
              {exhibitor.images.map((image, index) => (
                <a
                  key={index}
                  data-fslightbox={`gallery-${exhibitor.id}`}
                  data-type="image"
                  href={image.fullSizeImageUrl}
                  onClick={e => {
                    e.preventDefault();
                    // Initialize fslightbox for this gallery if it hasn't been initialized
                    if (window.fsLightbox) {
                      window.fsLightbox.open(index);
                    }
                  }}
                >
                  <div className="featured-image">
                    <img
                      className="avatar no-border"
                      src={image.imageUrl}
                      alt={`${exhibitor.name} image ${index + 1}`}
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </section>
    </div>
  );
};

export default ExhibitorProfile;
